import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  BanknoteIcon,
  ClockIcon,
  DollarSignIcon,
  PlusIcon,
  ReceiptIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useRefundRequestsQuery, useRefundsSummaryQuery } from "../api/queries";
import { CreateRefundRequestDialog } from "../ui/CreateRefundRequestDialog";
import { RefundRequestsFilter } from "../ui/RefundRequestsFilter";
import { RefundRequestsTable } from "../ui/RefundRequestsTable";

export function RefundRequestsPage() {
  const nav = useNavigate({ from: "/app/refunds" });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const canCreate = useActionPermission(["refunds:create"]);
  const search = useSearch({
    from: "/app/refunds",
  }) as Record<string, unknown>;

  const sortArray = useMemo((): Array<{
    field: string;
    dir: "asc" | "desc";
  }> => {
    const s = search.sort;
    if (Array.isArray(s) && s[0]) {
      const first = s[0] as { field?: string; dir?: string };
      return [
        {
          field: first.field ?? "requestedAt",
          dir: first.dir === "asc" ? "asc" : "desc",
        },
      ];
    }
    if (typeof s === "string" && s) {
      const field = s.startsWith("-") ? s.slice(1) : s;
      const dir: "asc" | "desc" = s.startsWith("-") ? "desc" : "asc";
      return [{ field, dir }];
    }
    return [{ field: "requestedAt", dir: "desc" }];
  }, [search.sort]);

  const query = useMemo(
    () => ({
      limit: Number(search.limit ?? 20),
      offset: Number(search.offset ?? 0),
      sort: sortArray,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
    }),
    [search.limit, search.offset, sortArray, search.filters],
  );

  const summaryQuery = useMemo(
    () => ({ filters: query.filters }),
    [query.filters],
  );

  const { data, isLoading } = useRefundRequestsQuery(query);
  const { data: summary, isLoading: summaryLoading } =
    useRefundsSummaryQuery(summaryQuery);
  const items = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const updateSearch = (updates: Record<string, unknown>) => {
    nav({ search: { ...search, ...updates } });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄຳຂໍຄືນເງິນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ຄຳຂໍຄືນເງິນ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ລາຍການຄຳຂໍຄືນເງິນທີ່ສົ່ງເຂົ້າລະບົບ
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <PlusIcon className="mr-2 size-4" />
              ສ້າງຄຳຮ້ອງຂໍຄືນເງິນ
            </Button>
          )}
        </div>

        <CreateRefundRequestDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
        />

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ຈຳນວນຄຳຂໍທັງໝົດ
                </CardTitle>
                <ReceiptIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {summaryLoading ? "..." : (summary?.totalCount ?? 0)}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">ຄຳຂໍ</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ລໍຖ້າກວດສອບ
                </CardTitle>
                <ClockIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {summaryLoading ? "..." : (summary?.requestedCount ?? 0)}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">ຄຳຂໍ</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ຍອດທີ່ຂໍຄືນທັງໝົດ
                </CardTitle>
                <BanknoteIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {summaryLoading
                  ? "..."
                  : (summary?.totalRefundAmountRequested ?? 0).toLocaleString(
                      "lo-LA",
                    )}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">ກີບ</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ຍອດທີ່ຄືນແລ້ວ
                </CardTitle>
                <DollarSignIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {summaryLoading
                  ? "..."
                  : (summary?.totalRefundAmountCompleted ?? 0).toLocaleString(
                      "lo-LA",
                    )}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">ກີບ</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <RefundRequestsFilter />
          <RefundRequestsTable
            data={items}
            isLoading={isLoading}
            offset={Number(search.offset ?? 0)}
            limit={Number(search.limit ?? 20)}
            totalCount={total}
            sortBy={query.sort?.[0]?.field ?? "requestedAt"}
            sortOrder={(query.sort?.[0]?.dir ?? "desc") as "asc" | "desc"}
            onPaginationChange={(offset, limit) =>
              updateSearch({ offset, limit })
            }
            onSortingChange={(id, desc) =>
              updateSearch({
                sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                offset: 0,
              })
            }
            onView={(id) => {
              nav({
                to: "/app/refunds/$id",
                params: { id },
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
